import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, MapPin, RotateCcw, Truck, ChevronDown, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { getRecentOrders, summarizeOrder, clearRecentOrders } from '../utils/recentOrders';
import { loadLatestOrder, getCurrentStepIndex, STEP_ORDER, type TrackedOrder } from '../utils/orderTracking';
// The personalized greeting replaces the marketing hero headline +
// tagline once the visitor has built up a relationship with us:
//
//   - first-time           → no saved order anywhere on the device
//                            → show the regular marketing hero
//   - just-delivered-once  → 1st return visit after a delivered order
//                            → still show the marketing hero, but the
//                              existing small WelcomeBackCard pill greets
//                              them and offers one-tap reorder
//   - repeat               → 2nd+ visit after a delivered order
//                            → drop the marketing pitch and the small
//                              pill; replace with a personal "Good
//                              morning, {firstName}" hero
//   - in-flight            → there's an active order from this device
//                            that hasn't reached the "delivered" step
//                            yet → personal greeting with a track CTA
//                            instead of a reorder CTA (regardless of
//                            visit count — this is the more urgent,
//                            more useful surface in that moment)
//
// All state is derived from localStorage so the system works without
// any auth. On a new device / private window the visitor naturally
// falls back to 'first-time', which is fine.
type HeroVariant = 'first-time' | 'just-delivered-once' | 'repeat' | 'in-flight';
const LAST_ORDER_KEY = '253gravel_last_order';
const VISITS_KEY = '253gravel_visits';
type SavedOrder = {
  items: {
    id?: string;
    category: string;
    product: string;
    quantity: number;
    pricePerYard: number;
  }[];
  addressInput?: string;
  name?: string;
  phone?: string;
  email?: string;
};
function getFirstName(fullName?: string): string | null {
  if (!fullName) return null;
  // First whitespace-separated token, stripped of trailing punctuation
  // and capped at ~16 chars so an obscure all-lowercase or all-caps
  // submission doesn't make the headline look broken. Reject single
  // letters and emoji-only inputs — they read as obviously wrong.
  const token = fullName.trim().split(/\s+/)[0] || '';
  const cleaned = token.replace(/[^\p{L}\p{M}'\-]/gu, '');
  if (cleaned.length < 2) return null;
  // Normalise casing: capital first letter, lowercase rest. Leaves
  // names with internal capitalisation (e.g. "McKay") alone if they
  // came in that way.
  const isAllSameCase = cleaned === cleaned.toLowerCase() || cleaned === cleaned.toUpperCase();
  const display = isAllSameCase ? cleaned[0].toUpperCase() + cleaned.slice(1).toLowerCase() : cleaned;
  return display.slice(0, 16);
}
function timeOfDayGreeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  if (h >= 17 && h < 22) return 'Good evening';
  return 'Hi';
}
// Rotating subhead questions for the repeat-visitor variant. Each phrase
// asks the same thing ("how can we help?") but in a different voice, so
// returning customers don't see the exact same line every visit. One is
// picked at mount time, so it's stable while the user is on the page
// but refreshes on the next visit.
const REPEAT_GREETING_QUESTIONS = ['What can we get you today?', 'What are we hauling for you today?', 'Where are we delivering this round?', 'Ready for another load?', "What's the next project?"];
function pickRepeatGreetingQuestion(): string {
  const i = Math.floor(Math.random() * REPEAT_GREETING_QUESTIONS.length);
  return REPEAT_GREETING_QUESTIONS[i];
}
export function usePersonalGreeting(): {
  variant: HeroVariant;
  firstName: string | null;
  savedOrder: SavedOrder | null;
  activeOrder: TrackedOrder | null;
  activeStepIdx: number;
} {
  const [variant, setVariant] = useState<HeroVariant>('first-time');
  const [firstName, setFirstName] = useState<string | null>(null);
  const [savedOrder, setSavedOrder] = useState<SavedOrder | null>(null);
  const [activeOrder, setActiveOrder] = useState<TrackedOrder | null>(null);
  const [activeStepIdx, setActiveStepIdx] = useState<number>(0);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_ORDER_KEY);
      if (!raw) {
        setVariant('first-time');
        return;
      }
      const parsed: SavedOrder = JSON.parse(raw);
      if (!parsed?.items?.length) {
        setVariant('first-time');
        return;
      }
      setSavedOrder(parsed);
      setFirstName(getFirstName(parsed.name));
      // In-flight order? Same source the WelcomeBackCard uses so the
      // two surfaces stay in lockstep.
      const latest = loadLatestOrder();
      if (latest) {
        const stepIdx = getCurrentStepIndex(latest);
        if (stepIdx < STEP_ORDER.length - 1) {
          setActiveOrder(latest);
          setActiveStepIdx(stepIdx);
          setVariant('in-flight');
          return;
        }
      }
      // No active order — pick between just-delivered-once and repeat
      // based on the same visits counter the WelcomeBackCard maintains.
      // `visits >= 2` means we have already given the customer at least
      // one shot at the small reorder pill on a prior visit, so it's
      // time to retire it and let the big greeting take the hero slot.
      const visits = parseInt(localStorage.getItem(VISITS_KEY) || '1', 10);
      setVariant(visits >= 2 ? 'repeat' : 'just-delivered-once');
    } catch {
      // Any localStorage / JSON issue → treat as first-time and let the
      // default marketing hero render. Failing closed never hurts here.
      setVariant('first-time');
    }
  }, []);
  return {
    variant,
    firstName,
    savedOrder,
    activeOrder,
    activeStepIdx
  };
}
// Friendly label for the active-order step. Matches the same vocab the
// /track page and WelcomeBackCard use so the customer sees consistent
// language across every touchpoint.
function stepLabelFor(stepIdx: number): string {
  switch (STEP_ORDER[stepIdx]) {
    case 'confirmed':
      return 'Order confirmed';
    case 'scheduled':
      return 'Scheduled for delivery';
    case 'dispatched':
      return 'Dispatched from yard';
    case 'en_route':
      return 'Driver on the way';
    default:
      return 'In progress';
  }
}
export function PersonalGreeting({
  variant,
  firstName,
  savedOrder,
  activeOrder,
  activeStepIdx
}: {
  variant: HeroVariant;
  firstName: string | null;
  savedOrder: SavedOrder | null;
  activeOrder: TrackedOrder | null;
  activeStepIdx: number;
}) {
  const {
    loadSavedCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  // Locked at first render so the phrase is stable while the user is on
  // the page — only a fresh page load rolls a new one.
  const [repeatQuestion] = useState<string>(() => pickRepeatGreetingQuestion());
  // Recent orders (up to 3) power the "Reorder last delivery" pulldown.
  // The collapsed tab shows the most recent; tapping the chevron reveals
  // the full list so the customer can reorder any of their standard loads.
  const [recentOrders, setRecentOrders] = useState<SavedOrder[]>([]);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  useEffect(() => {
    setRecentOrders(getRecentOrders());
  }, []);
  // Wipe all saved order history, then reload so the hero recomputes back to
  // the default marketing greeting (first-time state).
  const handleClearHistory = () => {
    clearRecentOrders();
    if (typeof window !== 'undefined') window.location.reload();
  };
  if (variant !== 'in-flight' && variant !== 'repeat') return null;
  // Display name fallback — if we couldn't pull a clean first name from
  // the saved order (rare but possible: blank, single letter, emoji),
  // skip the name and lead with the generic time-of-day greeting alone.
  const greetingHead = firstName ? `${timeOfDayGreeting()}, ${firstName}` : timeOfDayGreeting();
  const handleReorder = (order?: SavedOrder) => {
    const target = order || recentOrders[0] || savedOrder;
    if (!target) return;
    loadSavedCart(target.items.map(i => ({
      id: `${i.category}-${i.product}-${Date.now()}-${Math.random()}`,
      category: i.category,
      product: i.product,
      quantity: i.quantity,
      pricePerYard: i.pricePerYard
    })));
    if (target.addressInput) {
      setDeliveryInfo(target.addressInput, 0, null);
    }
    openCart();
  };
  // -------- IN-FLIGHT VARIANT --------
  // The customer just placed an order and the truck hasn't arrived yet.
  // The most useful surface in that moment is order status + a one-tap
  // jump to the live tracking page. Reorder is a secondary affordance.
  if (variant === 'in-flight' && activeOrder) {
    const stepLabel = stepLabelFor(activeStepIdx);
    const shortAddress = (activeOrder.address || '').split(',')[0].trim();
    const totalYards = activeOrder.items.reduce((s, i) => s + i.quantity, 0);
    const itemSummary = activeOrder.items.length > 1 ? `${totalYards} yds across ${activeOrder.items.length} materials` : `${activeOrder.items[0].quantity} yds ${activeOrder.items[0].product}`;
    return <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }} className="mb-4 md:mb-6" data-id="element-1403">
        <div className="inline-flex items-center gap-2 mb-4 md:mb-5 px-3 py-1.5 rounded-full bg-[#059F40]/15 border border-[#059F40]/35 backdrop-blur-md" data-id="element-1404">
          <span className="relative flex h-2 w-2" data-id="element-1405">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#059F40] opacity-75" data-id="element-1406" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#059F40]" data-id="element-1407" />
          </span>
          <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#22c95c]" data-id="element-1408">
            {stepLabel}
          </span>
        </div>

        <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1] mb-3 md:mb-4" data-id="element-1409">
          {greetingHead}
          <span className="block mt-1 md:mt-2 text-[#059F40]" data-id="element-1410">
            Your load is on the way.
          </span>
        </h1>

        {/* Delivery details stack on their own lines so a long address
                                 doesn't break mid-word on mobile. White arrow on the address
                                 line reinforces the "material → destination" flow. */}
        <div className="mb-5 md:mb-6 max-w-2xl space-y-1.5" data-id="element-1411">
          <p className="text-lg md:text-xl font-semibold font-sub text-white/95" data-id="element-1412">
            {itemSummary}
          </p>
          {shortAddress && <p className="text-lg md:text-xl font-semibold font-sub text-white/95 flex items-baseline gap-2 justify-center md:justify-start" data-id="element-1413">
              <span className="text-white shrink-0" aria-hidden="true" data-id="element-1414">
                →
              </span>
              <span data-id="element-1415">{shortAddress}</span>
            </p>}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center items-center sm:items-stretch mb-2 md:justify-start" data-id="element-1416">
          <Link to={`/track/${encodeURIComponent(activeOrder.orderId)}`} className="group inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#059F40] hover:bg-[#048F3A] text-white font-bold text-base transition-colors shadow-[0_0_18px_rgba(5,159,64,0.35)]" aria-label="Track your delivery" data-id="element-1417">
            <MapPin className="w-4 h-4" data-id="element-1418" />
            Track delivery
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-1419" />
          </Link>
          <span className="text-sm text-white/70 text-center md:text-left sm:ml-1" data-id="element-1420">
            Need another load? Build a new order below.
          </span>
        </div>
      </motion.div>;
  }
  // -------- REPEAT VARIANT --------
  // No active order, but they've come back at least once since their
  // last delivery. They already know what we do; they're here to
  // buy again. Greet by name and put a reorder shortcut front and centre.
  if (!savedOrder) return null;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: [0.16, 1, 0.3, 1]
  }} className="mb-4 md:mb-6" data-id="element-1421">
      <div className="inline-flex items-center gap-2 mb-4 md:mb-5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md" data-id="element-1422">
        <Truck className="w-3.5 h-3.5 text-[#22c95c]" data-id="element-1423" />
        <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/90" data-id="element-1424">
          Welcome back
        </span>
      </div>

      <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1] mb-3 md:mb-4" data-id="element-1425">
        {greetingHead}.
        <span className="block mt-1 md:mt-2 text-[#059F40]" data-id="element-1426">
          {repeatQuestion}
        </span>
      </h1>

      <p className="text-lg md:text-xl text-white/85 font-semibold font-sub mb-5 md:mb-6 max-w-2xl" data-id="element-1427">
        Pick up where you left off — same address, same materials, or build a
        new order below.
      </p>

      {/* Inline reorder shortcut. With a single saved order it stays a
                          one-tap "Reorder last delivery" button. With 2–3 recent
                          orders it becomes a tab that pulls down an inline drawer so
                          the customer can pick which standard load to reorder. */}
      {(() => {
      const headlineOrder = recentOrders[0] || savedOrder;
      if (!headlineOrder) return null;
      const hasMultiple = recentOrders.length > 1;
      return <div className="flex justify-center md:justify-start" data-id="element-1428">
            <div className="w-full max-w-sm rounded-xl bg-white/10 border border-white/20 backdrop-blur-md overflow-hidden" data-id="element-1429">
              {/* Tab / header row */}
              <button type="button" onClick={() => hasMultiple ? setReorderOpen(v => !v) : handleReorder()} aria-expanded={hasMultiple ? reorderOpen : undefined} className="group w-full inline-flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left" data-id="element-1430">
                <span className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-full bg-[#059F40]/20 border border-[#059F40]/40" data-id="element-1431">
                  <RotateCcw className="w-4 h-4 text-[#22c95c]" data-id="element-1432" />
                </span>
                <span className="flex flex-col items-start min-w-0 flex-1" data-id="element-1433">
                  <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-white/60" data-id="element-1434">
                    {hasMultiple ? 'Reorder last deliveries' : 'Reorder last delivery'}
                  </span>
                  <span className="text-sm sm:text-base font-bold text-white truncate max-w-full" data-id="element-1435">
                    {hasMultiple ? 'Choose from your recent loads' : summarizeOrder(headlineOrder)}
                  </span>
                </span>
                {hasMultiple ? <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white/70 shrink-0 ml-1" data-id="element-1436">
                    <span className="hidden sm:inline" data-id="element-1437">
                      {recentOrders.length} recent
                    </span>
                    <ChevronDown className={`w-4 h-4 text-[#22c95c] transition-transform duration-300 ${reorderOpen ? 'rotate-180' : ''}`} data-id="element-1438" />
                  </span> : <span className="inline-flex items-center gap-1 text-sm font-bold text-[#22c95c] shrink-0 ml-1" data-id="element-1439">
                    Reorder
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-1440" />
                  </span>}
              </button>

              {/* Inline pulldown drawer — lists up to 3 recent orders. */}
              {hasMultiple && <AnimatePresence initial={false} data-id="element-1441">
                  {reorderOpen && <motion.div key="reorder-drawer" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden border-t border-white/15" role="list" aria-label="Recent orders" data-id="element-1442">
                      {recentOrders.map((order, i) => {
                const shortAddress = (order.addressInput || '').split(',')[0].trim();
                return <button key={i} type="button" role="listitem" onClick={() => {
                  setReorderOpen(false);
                  handleReorder(order);
                }} aria-label={`Reorder ${summarizeOrder(order)}`} className="group w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#059F40]/15 transition-colors text-left border-b border-white/10 last:border-b-0" data-id="element-1443">
                            <span className="flex flex-col items-start min-w-0 flex-1" data-id="element-1444">
                              <span className="text-sm font-bold text-white truncate max-w-full" data-id="element-1445">
                                {summarizeOrder(order)}
                              </span>
                              {shortAddress && <span className="text-[11px] text-white/55 truncate max-w-full" data-id="element-1446">
                                  {shortAddress}
                                </span>}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#22c95c] shrink-0" data-id="element-1447">
                              Reorder
                              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-1448" />
                            </span>
                          </button>;
              })}

                      {/* Subtle clear-history footer. Two-step confirm so a
                            stray tap doesn't wipe the customer's saved loads. */}
                      <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-white/10 bg-black/10" data-id="element-1449">
                        {confirmClear ? <>
                            <span className="text-[11px] text-white/55 mr-auto" data-id="element-1450">
                              Clear saved orders?
                            </span>
                            <button type="button" onClick={() => setConfirmClear(false)} className="text-[11px] font-semibold text-white/60 hover:text-white px-2 py-1 rounded transition-colors" data-id="element-1451">
                              Cancel
                            </button>
                            <button type="button" onClick={handleClearHistory} className="text-[11px] font-bold text-red-300 hover:text-red-200 px-2 py-1 rounded hover:bg-red-500/10 transition-colors" data-id="element-1452">
                              Clear
                            </button>
                          </> : <button type="button" onClick={() => setConfirmClear(true)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/40 hover:text-white/70 transition-colors" aria-label="Clear order history" data-id="element-1453">
                            <Trash2 className="w-3 h-3" data-id="element-1454" />
                            Clear history
                          </button>}
                      </div>
                    </motion.div>}
                </AnimatePresence>}
            </div>
          </div>;
    })()}
    </motion.div>;
}